import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '@app/store';
import { fetchFilledForms, deleteFilledForm } from './filledFormSlice';
import { DataTable, DataFilter } from '@components/ui';
import type { Column } from '@components/ui/DataTable';
import { FilledFormOutputDTO } from '@features/filledForm/types';
import { LoadingSkeleton } from '@components/ui/LoadingSkeleton';
import { ErrorDisplay } from '@components/ui/ErrorDisplay';
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
} from '@mui/material';
import { Visibility as VisibilityIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import {
  getFilledFormViewState,
  isFilledFormMissionComplete,
  ViewStateLabels,
} from '@features/filledForm/types';
import { fetchPhaseForms } from '@features/phaseForm/phaseFormSlice';

/**
 * Fundo translúcido para forms ainda não cumpridos.
 * Densidade ajustada de 0.03 para 0.10 (pedido do Sérgio 25/05 — em telas
 * com pouca luz o 3% ficava quase invisível).
 */
const PENDING_ROW_BACKGROUND = 'rgba(23, 119, 207, 0.10)';
const COMPLETE_ROW_BACKGROUND = '#FFFFFF';

/**
 * Quando o cliente ainda não preencheu o form, o "Nome" fica N/A na lista
 * — o Sérgio reportou em 25/05 que isso não permite identificar pra quem
 * o link foi enviado. O backend cria filledforms via batch-whatsapp com
 * `userEmail = "<phone>@whatsapp.user"`, então extraímos e formatamos o
 * número como fallback.
 */
function formatWhatsappFromEmail(userEmail?: string): string | null {
  if (!userEmail || !userEmail.includes('@whatsapp.user')) return null;
  const raw = userEmail.split('@')[0].replace(/\D/g, '');
  if (!raw) return null;
  // +55 DDD 9XXXX-XXXX  →  +55 (11) 98765-4321
  if (raw.length === 13 && raw.startsWith('55')) {
    return `+${raw.slice(0, 2)} (${raw.slice(2, 4)}) ${raw.slice(4, 9)}-${raw.slice(9)}`;
  }
  // sem código de país, 11 dígitos: (11) 98765-4321
  if (raw.length === 11) {
    return `(${raw.slice(0, 2)}) ${raw.slice(2, 7)}-${raw.slice(7)}`;
  }
  // fallback: devolve com prefixo + pra deixar claro que é phone
  return `+${raw}`;
}

function resolveDisplayName(item: FilledFormOutputDTO): string {
  if (item.data && Array.isArray(item.data)) {
    const fromData = getFirstFieldValue(item.data);
    if (fromData && fromData !== 'N/A') return fromData;
  }
  const fromPhone = formatWhatsappFromEmail((item as any).userEmail);
  if (fromPhone) return fromPhone;
  return 'N/A';
}

const getFirstFieldValue = (formData: any[]): string => {
  if (!formData || !Array.isArray(formData)) {
    return 'N/A';
  }

  // Função recursiva para encontrar o primeiro valor primitivo
  const findFirstPrimitiveValue = (obj: any): string | null => {
    if (obj === null || obj === undefined) {
      return null;
    }

    // Se for um array, procura no primeiro elemento
    if (Array.isArray(obj)) {
      if (obj.length > 0) {
        const result = findFirstPrimitiveValue(obj[0]);
        if (result) return result;
      }
      return null;
    }

    // Se for um objeto, procura recursivamente em suas propriedades
    if (typeof obj === 'object') {
      const keys = Object.keys(obj);
      for (const key of keys) {
        const value = obj[key];
        if (value !== null && value !== undefined && value !== '') {
          // Se o valor for primitivo, retorna
          if (typeof value !== 'object') {
            return String(value);
          }
          // Se for objeto ou array, procura recursivamente
          const result = findFirstPrimitiveValue(value);
          if (result) return result;
        }
      }
      return null;
    }

    // Se for um valor primitivo, retorna
    if (typeof obj !== 'object' && obj !== null && obj !== '') {
      return String(obj);
    }

    return null;
  };

  // Procura o primeiro campo que não seja do tipo PAGE e tenha dados
  for (const item of formData) {
    if (item.field?.type !== 'PAGE' && item.data) {
      const result = findFirstPrimitiveValue(item.data);
      if (result) {
        return result;
      }
    }
  }

  return 'N/A';
};

const FilledFormList = () => {
  console.log('🔄 Renderizando FilledFormList');
  
  const dispatch = useDispatch<AppDispatch>();
  const { items, loading, error } = useSelector((state: RootState) => {
    console.log('🔍 Selector executando, loading:', state.filledForm.loading);
    return state.filledForm;
  });
  const { items: phaseForms } = useSelector((state: RootState) => state.phaseForm);
  
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [filters, setFilters] = useState<Record<string, string>>({});
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const isLoadingRef = useRef(false);
  const initialLoadDoneRef = useRef(false);
  const navigate = useNavigate();

  const loadData = useCallback(async (currentFilters: Record<string, string>) => {
    // Evita chamadas simultâneas
    if (isLoadingRef.current) {
      console.log('🚫 Requisição em andamento, ignorando nova chamada');
      return;
    }

    // Evita chamadas após o carregamento inicial
    if (initialLoadDoneRef.current && Object.keys(currentFilters).length === 0) {
      console.log('🚫 Ignorando chamada sem filtros após carregamento inicial');
      return;
    }

    console.log('📡 Iniciando requisição fetchFilledForms');
    isLoadingRef.current = true;

    try {
      await dispatch(fetchFilledForms(currentFilters));
      console.log('✅ Requisição fetchFilledForms completada');
      setIsInitialLoad(false);
      initialLoadDoneRef.current = true;
    } catch (error) {
      console.error('❌ Erro na requisição:', error);
    } finally {
      isLoadingRef.current = false;
    }
  }, [dispatch]);

  const handleFilter = useCallback((newFilters: Record<string, string>) => {
    console.log('🔍 Novos filtros aplicados:', newFilters);
    setFilters(newFilters);
  }, []);

  // Efeito único para carregamento inicial
  useEffect(() => {
    if (!initialLoadDoneRef.current) {
      loadData({});
    }
  }, [loadData]);

  // Efeito separado para mudanças nos filtros
  useEffect(() => {
    if (initialLoadDoneRef.current && Object.keys(filters).length > 0) {
      loadData(filters);
    }
  }, [filters, loadData]);

  // Adicione este useEffect para carregar os formulários
  useEffect(() => {
    dispatch(fetchPhaseForms());
  }, [dispatch]);

  const handleDelete = async (id: string) => {
    console.log('🗑️ Iniciando delete do item:', id);
    try {
      await dispatch(deleteFilledForm(id));
      setDeleteId(null);
      console.log('🔄 Recarregando lista após delete');
      dispatch(fetchFilledForms(filters));
    } catch (error) {
      console.error('❌ Erro ao excluir:', error);
    }
  };

  const filterFields = [
    {
      name: 'name',
      label: 'Nome',
      type: 'text' as const,
    },
    {
      name: 'email',
      label: 'Email',
      type: 'text' as const,
    }
  ];

  const columns: Column<FilledFormOutputDTO>[] = [
     {
      image:'photoUrl',
      key: 'processId',
      header: 'Nome / WhatsApp',
      render(_, item) {
        return resolveDisplayName(item);
      },
    },
    {
      key: 'phaseForm',
      header: 'Tipo de Formulário',
      render(_, item) {
        const phaseForm = phaseForms.find(form => form._id === item.phaseFormId);
        return phaseForm?.name || 'N/A';
      },
    },
   
    {
      key: 'status',
      header: 'Status',
      render(_, item) {
        return ViewStateLabels[getFilledFormViewState(item)];
      },
    },
    {
      key: 'receivedAt',
      header: 'Recebido em',
      render(_, item) {
        return item.createdAt
          ? new Date(item.createdAt).toLocaleString('pt-BR')
          : '-';
      },
    },
    {
      key: 'files',
      header: 'Anexos',
      render(_, item) {
        const count = (item as any).attachmentsCount ?? 0;
        return String(count).padStart(2, '0');
      },
    },
    {
      key: 'appoitment',
      header: 'Agendamento',
      render(_, item) {
        return '-';
      },
    },
    // "Criação" removida — era duplicata exata de "Recebido em" (createdAt).
    {
      key: 'completedAt',
      header: 'Preenchimento',
      render(_, item: any) {
        return item.completedAt ? new Date(item.completedAt).toLocaleString('pt-BR') : '-';
      },
    },
  ];

  
  if (isInitialLoad && loading) {
    return <LoadingSkeleton />;
  }

  console.log('pahses', phaseForms,items)

  return (
    <Box sx={{  p: 3 }}>
      {/* <Typography variant="h5" gutterBottom>
        Formulários Preenchidos
      </Typography>

      {error && <ErrorDisplay error={error} />}

      <DataFilter
        fields={filterFields}
        onFilter={handleFilter}
        autoSubmit={true}
        debounceTime={500}
      /> */}

      <DataTable
        data={items}
        columns={columns}
        newFormat
        basePath="filled-forms"
        hide_add
        getRowStyle={(item) => ({
          background: isFilledFormMissionComplete(item)
            ? COMPLETE_ROW_BACKGROUND
            : PENDING_ROW_BACKGROUND,
        })}
        // onDelete={(id) => {
        //   console.log('🗑️ Solicitação de delete para id:', id);
        //   setDeleteId(id);
        // }}
        hide_actions={false}
        title='Clientes'
        subtitle='Lista de clientes que preencheram formulários'
        hideEdit={true}
        loading={loading}
        error={error}
        loadingComponent={<LoadingSkeleton title={false} filter={false} />}
        actions={[
          {
            label: "Visualizar",
            icon: <VisibilityIcon />,
            onClick: (item) => navigate(`/filled-forms/view/${item._id}`),
          }
        ]}
      />

      <Dialog
        open={!!deleteId}
        onClose={() => setDeleteId(null)}
      >
        <DialogTitle>Confirmar Exclusão</DialogTitle>
        <DialogContent>
          <Typography>
            Tem certeza que deseja excluir este formulário preenchido?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteId(null)}>Cancelar</Button>
          <Button
            onClick={() => deleteId && handleDelete(deleteId)}
            color="error"
            variant="contained"
          >
            Excluir
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FilledFormList; 
